/**
 * Context resolution for the "Latest Q&A" audit feed.
 *
 * Sidebar chat docs only store a `uid` and an optional `loanId`, so both the
 * organization and the loan behind an exchange have to be inferred from other
 * collections. Everything here reads Firestore directly, like the rest of this
 * dashboard, and takes the `db` handle so prod/dev can be swapped per call.
 */
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { checklistTypeToLabel } from "./loanTypeLabels";

const ADDY_APP_URL_PROD = "https://app.addy.so";
const ADDY_APP_URL_DEV = "https://devmail.addy.so";

// Firestore "in" queries accept at most 10 values.
const IN_CHUNK_SIZE = 10;

// Support accounts belong to hundreds of orgs; probing every one is too costly.
const MAX_ORG_CANDIDATES_PER_LOAN = 12;

// Beyond this we refuse to guess an org, so the feed never shows a wrong one.
const MAX_UNAMBIGUOUS_ORGS = 3;

export const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));

  return out;
};

export const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }

  return "";
};

export const orgDisplayName = (org) =>
  org.name || org.organizationName || org.companyName || org.id;

export const loanAppUrl = (env, loanId) => {
  if (!loanId) return "";
  const base = env === "dev" ? ADDY_APP_URL_DEV : ADDY_APP_URL_PROD;

  return `${base}/loans?tab=board&loan=${loanId}&tabValue=processingChecklist`;
};

const loanCacheKey = (orgId, subcollection, loanId) =>
  `${orgId}/${subcollection}/${loanId}`;

const getLoanNumber = (loan) =>
  firstNonEmpty(
    loan?.number,
    loan?.loanNumber,
    loan?.applicationNumber,
    loan?.losId
  );

const getLoanType = (loan) =>
  firstNonEmpty(loan?.type, loan?.loanType, loan?.purpose, loan?.loanPurpose);

const getLoanStage = (loan) =>
  firstNonEmpty(
    loan?.stage,
    loan?.loanStage,
    loan?.boardColumn,
    loan?.boardColumnName,
    loan?.status
  );

const getBorrowerName = (loan) => {
  const borrower = Array.isArray(loan?.borrowers) ? loan.borrowers[0] : null;
  const fromParts = borrower
    ? [borrower.firstName, borrower.lastName].filter(Boolean).join(" ").trim()
    : "";

  return firstNonEmpty(
    fromParts,
    borrower?.name,
    borrower?.fullName,
    borrower?.email,
    loan?.borrowerName,
    loan?.borrowerFullName,
    loan?.primaryBorrowerName,
    loan?.borrower
  );
};

const getPropertyAddress = (loan) =>
  firstNonEmpty(
    loan?.address,
    loan?.propertyAddress,
    loan?.subjectPropertyAddress,
    loan?.property?.address
  );

const formatLoanAmount = (amount) => {
  const numeric = Number(amount);
  if (!numeric || Number.isNaN(numeric)) return "";

  return numeric.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

/** Compact loan fields we attach to each Q&A exchange for the audit feed. */
export function summarizeLoan(loanId, loan) {
  return {
    loanId,
    loanNumber: getLoanNumber(loan),
    loanType: checklistTypeToLabel(getLoanType(loan)),
    loanStage: getLoanStage(loan),
    loanAmount: formatLoanAmount(loan?.amount ?? loan?.loanAmount),
    borrowerName: getBorrowerName(loan),
    propertyAddress: getPropertyAddress(loan),
    los: firstNonEmpty(loan?.los),
  };
}

/** Empty caches for one environment's feed: raw doc reads plus resolved loans. */
export const createLoanCache = () => ({ docs: new Map(), loans: new Map() });

/** Reads users/{uid} docs for the given uids, keyed by uid. */
async function fetchUserDocs(db, uids) {
  const byUid = new Map();
  for (const group of chunk(uids, IN_CHUNK_SIZE)) {
    const snap = await getDocs(
      query(collection(db, "users"), where(documentId(), "in", group))
    );
    snap.forEach((d) => byUid.set(d.id, d.data() || {}));
  }

  return byUid;
}

/**
 * Collects candidate organization IDs per uid, most trustworthy first, and the
 * emails found along the way. Membership docs cover most users, but an org
 * owner/creator frequently has no organization-members doc at all — and some
 * users/{uid} docs are activity stubs with no email to join on. Both were
 * sources of "unknown org".
 */
async function collectOrgCandidates(db, uids, userDocs, orgNameById) {
  const candidatesByUid = new Map(uids.map((uid) => [uid, []]));
  const memberEmailByUid = new Map();

  const addCandidate = (uid, orgId) => {
    if (!uid || !orgId) return;
    const candidates = candidatesByUid.get(uid);
    if (!candidates || candidates.includes(orgId)) return;
    candidates.push(orgId);
  };

  const emailByUid = new Map(
    uids.map((uid) => [uid, (userDocs.get(uid)?.email || "").trim()])
  );
  const uidsByEmail = new Map();
  for (const [uid, email] of emailByUid.entries()) {
    if (!email) continue;
    const key = email.toLowerCase();
    uidsByEmail.set(key, [...(uidsByEmail.get(key) || []), uid]);
  }
  const uidsForEmail = (email) =>
    uidsByEmail.get((email || "").toLowerCase()) || [];

  // Stored emails are not consistently lower-cased, so match both spellings.
  const emailTerms = [
    ...new Set(
      [...emailByUid.values()].flatMap((email) =>
        email ? [email, email.toLowerCase()] : []
      )
    ),
  ];

  // Membership, matched by uid (immune to email spelling) and by email (covers
  // the many member docs where userId was never written).
  const members = [];
  for (const group of chunk(uids, IN_CHUNK_SIZE)) {
    const snap = await getDocs(
      query(collection(db, "organization-members"), where("userId", "in", group))
    );
    snap.forEach((d) => {
      const data = d.data();
      if (data.email && !memberEmailByUid.has(data.userId)) {
        memberEmailByUid.set(data.userId, data.email);
      }
      members.push({
        uids: [data.userId],
        orgId: data.organizationId,
        status: data.status,
        isAddy: data.isAddy,
      });
    });
  }
  for (const group of chunk(emailTerms, IN_CHUNK_SIZE)) {
    const snap = await getDocs(
      query(collection(db, "organization-members"), where("email", "in", group))
    );
    snap.forEach((d) => {
      const data = d.data();
      members.push({
        uids: uidsForEmail(data.email),
        orgId: data.organizationId,
        status: data.status,
        isAddy: data.isAddy,
      });
    });
  }
  const isAcceptedMember = (member) =>
    !member.status || member.status === "accepted";

  // Organizations this user owns, by uid and by owner email.
  const owned = [];
  for (const group of chunk(uids, IN_CHUNK_SIZE)) {
    const snap = await getDocs(
      query(
        collection(db, "organizations"),
        where("defaultForUserId", "in", group)
      )
    );
    snap.forEach((d) => {
      orgNameById.set(d.id, orgDisplayName({ id: d.id, ...d.data() }));
      owned.push({ uids: [d.data().defaultForUserId], orgId: d.id });
    });
  }
  for (const group of chunk(emailTerms, IN_CHUNK_SIZE)) {
    const snap = await getDocs(
      query(collection(db, "organizations"), where("ownerEmail", "in", group))
    );
    snap.forEach((d) => {
      orgNameById.set(d.id, orgDisplayName({ id: d.id, ...d.data() }));
      owned.push({ uids: uidsForEmail(d.data().ownerEmail), orgId: d.id });
    });
  }

  // Confidence order: a real membership, then support memberships added for
  // Addy staff, then owned orgs, then pending invites.
  const accepted = members.filter(isAcceptedMember);
  const ordered = [
    ...accepted.filter((member) => !member.isAddy),
    ...accepted.filter((member) => member.isAddy),
    ...owned,
    ...members.filter((member) => !isAcceptedMember(member)),
  ];
  for (const { uids: candidateUids, orgId } of ordered) {
    for (const uid of candidateUids) {
      addCandidate(uid, orgId);
    }
  }

  // Last resort: the invite link stored on the user doc carries an org ID.
  for (const uid of uids) {
    if (candidatesByUid.get(uid)?.length) continue;
    const user = userDocs.get(uid) || {};
    const signupLink = `${user.signupUrl || ""} ${user.signupPath || ""}`;
    const orgIdFromLink = signupLink.match(/organizationId=([\w-]+)/)?.[1];
    addCandidate(uid, firstNonEmpty(user.organizationId, orgIdFromLink));
  }

  return { candidatesByUid, memberEmailByUid };
}

/** Fills in display names for candidate orgs we have not read yet. */
async function fetchOrgNames(db, orgIds, orgNameById) {
  const missing = [...new Set(orgIds)].filter(
    (orgId) => orgId && !orgNameById.has(orgId)
  );

  for (const group of chunk(missing, IN_CHUNK_SIZE)) {
    const snap = await getDocs(
      query(collection(db, "organizations"), where(documentId(), "in", group))
    );
    snap.forEach((d) =>
      orgNameById.set(d.id, orgDisplayName({ id: d.id, ...d.data() }))
    );
  }

  return orgNameById;
}

/**
 * Resolves uid -> { email, name, orgId, orgIds, orgName }, memoizing into
 * `cache` so repeated pages don't re-read the same users. `orgIds` keeps every
 * candidate so a loan lookup can pick the org that actually holds the loan.
 * Org display names land in the shared `orgNameCache`.
 *
 * When a user belongs to more orgs than we can sensibly choose between (support
 * accounts sit in hundreds), `orgId` stays empty rather than naming a random
 * one; a loan match can still pin the org down later.
 */
export async function resolveAuthors(db, uids, cache, orgNameCache) {
  const missing = [...new Set(uids.filter((uid) => uid && !cache.has(uid)))];
  if (missing.length === 0) return cache;

  const userDocs = await fetchUserDocs(db, missing);
  const { candidatesByUid, memberEmailByUid } = await collectOrgCandidates(
    db,
    missing,
    userDocs,
    orgNameCache
  );
  const probedOrgIds = [...candidatesByUid.values()].flatMap((orgIds) =>
    orgIds.slice(0, MAX_ORG_CANDIDATES_PER_LOAN)
  );
  await fetchOrgNames(db, probedOrgIds, orgNameCache);

  for (const uid of missing) {
    const user = userDocs.get(uid) || {};
    const orgIds = candidatesByUid.get(uid) || [];
    const orgId = orgIds.length > MAX_UNAMBIGUOUS_ORGS ? "" : orgIds[0] || "";
    cache.set(uid, {
      email: firstNonEmpty(user.email, memberEmailByUid.get(uid)),
      name: firstNonEmpty(user.name, user.displayName),
      orgId,
      orgIds,
      orgName: orgNameCache.get(orgId) || "",
    });
  }

  return cache;
}

/**
 * Reads one loan document, caching the result — including a miss — so the same
 * org/loan pair is never read twice within a session.
 */
async function readLoanDoc(db, orgId, subcollection, loanId, cache) {
  const key = loanCacheKey(orgId, subcollection, loanId);
  if (cache.has(key)) return cache.get(key);

  try {
    const snap = await getDoc(
      doc(db, "organizations", orgId, subcollection, loanId)
    );
    cache.set(key, snap.exists() ? snap.data() : null);
  } catch (err) {
    console.warn("[AskAddyChats] loan lookup failed", key, err);
    cache.set(key, null);
  }

  return cache.get(key);
}

/**
 * Probe order for one loan: the best candidate on its own first — which is all
 * it takes for a user with a single org — then the remaining candidates in
 * parallel, and only then the heavier loans collection.
 */
function loanProbeGroups(orgIds) {
  const [best, ...rest] = orgIds;

  return ["loans-thin", "loans"].flatMap((subcollection) =>
    [[best], rest].filter((group) => group.length).map((group) => ({
      subcollection,
      orgIds: group,
    }))
  );
}

/** Finds which of the candidate orgs holds the loan, or null if none does. */
async function findLoanInOrgs(db, loanId, orgIds, cache) {
  for (const { subcollection, orgIds: group } of loanProbeGroups(orgIds)) {
    const reads = await Promise.all(
      group.map(async (orgId) => {
        const data = await readLoanDoc(db, orgId, subcollection, loanId, cache);

        return data ? { orgId, data } : null;
      })
    );
    const hit = reads.find(Boolean);
    if (hit) {
      return { orgId: hit.orgId, loan: summarizeLoan(loanId, hit.data) };
    }
  }

  return null;
}

/**
 * Orders the orgs worth probing for each loan referenced by this page.
 *
 * A loan ID belongs to exactly one org, so evidence is pooled per loan: the
 * candidates of the most narrowly-scoped author come first, which means a chat
 * from a support account sitting in hundreds of orgs is resolved for free when
 * the same loan also appears in a chat from someone with a single org.
 */
function buildLoanProbes(pairs) {
  const candidateListsByLoan = new Map();
  for (const { loanId, orgIds } of pairs) {
    if (!loanId || !orgIds?.length) continue;
    candidateListsByLoan.set(loanId, [
      ...(candidateListsByLoan.get(loanId) || []),
      orgIds,
    ]);
  }

  const probes = new Map();
  for (const [loanId, lists] of candidateListsByLoan.entries()) {
    const ordered = [];
    for (const list of [...lists].sort((a, b) => a.length - b.length)) {
      for (const orgId of list) {
        if (!ordered.includes(orgId)) ordered.push(orgId);
      }
    }
    probes.set(loanId, ordered.slice(0, MAX_ORG_CANDIDATES_PER_LOAN));
  }

  return probes;
}

/**
 * Resolves the loan behind every loan-scoped pair on the page. Returns a map of
 * loanId -> { orgId, loan }; `cache` memoizes both document reads and resolved
 * loans so paging through the feed does not repeat work.
 */
export async function resolveLoansForPairs(db, pairs, cache) {
  const probes = [...buildLoanProbes(pairs).entries()].filter(
    ([loanId]) => !cache.loans.has(loanId)
  );

  await Promise.all(
    probes.map(async ([loanId, orgIds]) => {
      const match = await findLoanInOrgs(db, loanId, orgIds, cache.docs);
      if (match) cache.loans.set(loanId, match);
    })
  );

  return cache.loans;
}

/**
 * Merges a resolved loan onto a Q&A pair. Finding the loan also pins down which
 * candidate org the chat belongs to, so the org shown is corrected here.
 */
export function attachLoanMeta(pair, loansByLoanId, orgNameCache) {
  const resolution = pair.loanId ? loansByLoanId.get(pair.loanId) : null;
  if (!resolution) return pair;

  return {
    ...pair,
    ...resolution.loan,
    orgId: resolution.orgId,
    orgName: orgNameCache.get(resolution.orgId) || pair.orgName || "",
  };
}
